import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { ScreenWrapper } from '../../components/Wrappers/ScreenWrapper';
import { Grid2 } from '@mui/material';
import { TMaterialStyles } from '../../theme';
import { useNavigate } from 'react-router';
import { ERoutes } from '../../enums';
import {
  TQuizz,
  TQuizzAnswer,
  TQuizzResult,
  useQuizzStore,
} from '../../stores';
import _ from 'lodash';
import { QuizzAnswerButton } from '../../components/Quizz/QuizzAnswerButton';
import {
  getQuizzAvailableState,
  submitQuizzAnswer,
  submitQuizzAnswerBulk,
} from '../../queries';
import { ConfirmModal } from '../../components/Modals/ConfirmModal';
import { ClaimRewardsQuizzModal } from '../../components/Modals/ClaimRewardsQuizzModal';
import { ContentWrapperWithClose } from '../../components/Wrappers/ContentWrapperWithClose';
import TypographyWithShadow from '../../components/Typography/TypographyWithShadow';
import { useTranslation } from 'react-i18next';
import { ButtonOutlined } from '../../components/Buttons/ButtonOutlined';
import { useLoader } from '../../hooks';

type TQuizzStateAction =
  | { type: 'ON_RESET' }
  | {
      type: 'INITIALIZE';
      initialQuizz: TQuizz;
    }
  | {
      type: 'SELECT_ANSWER';
      selectedIndex: number;
    }
  | {
      type: 'SET_RIGHT_ANSWER';
      selectedIndex: number;
      rightIndex: number;
      rightFact: string;
    }
  | {
      type: 'SET_NEXT_QUIZZ';
      quizz: TQuizz;
    }
  | {
      type: 'SET_QUIZZ_RESULT';
      result: TQuizzResult;
    };

type TQuizzState = {
  quizzIndex: number;
  quizz: TQuizz | null;
  selectedAnswerIndex: number | null;
  rightAnswerIndex: number | null;
  rightAnswerFact: string | null;
  answers: TQuizzAnswer[];
  result: TQuizzResult | null;
};

const initialQuizzState: TQuizzState = {
  quizzIndex: 0,
  quizz: null,
  selectedAnswerIndex: null,
  rightAnswerIndex: null,
  rightAnswerFact: null,
  answers: [],
  result: null,
};

const quizzReducer = (state: TQuizzState, action: TQuizzStateAction) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        quizz: action.initialQuizz,
      };
    case 'SET_RIGHT_ANSWER':
      if (!state.quizz) {
        return initialQuizzState;
      }

      return {
        ...state,
        selectedAnswerIndex: action.selectedIndex,
        rightAnswerIndex: action.rightIndex,
        rightAnswerFact: action.rightFact,
        answers: [
          ...state.answers,
          { quizId: state.quizz.id, selectedAnswer: action.selectedIndex },
        ],
      };
    case 'SELECT_ANSWER':
      return {
        ...state,
        selectedAnswerIndex: action.selectedIndex,
      };
    case 'SET_NEXT_QUIZZ':
      return {
        ...state,
        selectedAnswerIndex: null,
        rightAnswerIndex: null,
        rightAnswerFact: null,
        quizz: action.quizz,
        quizzIndex: state.quizzIndex + 1,
      };
    case 'SET_QUIZZ_RESULT':
      return {
        ...state,
        result: action.result,
      };
    case 'ON_RESET':
      return initialQuizzState;
    default:
      throw new Error(`Unknown action ${action}`);
  }
};

const QuizzScreen: React.FC = () => {
  const { quizzes } = useQuizzStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isConfirmQuitModalOpen, setIsConfirmQuitModalOpen] = useState(false);
  const [isClaimRewardModalOpen, setIsClaimRewardModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useLoader(false);

  const [quizzState, dispatchQuizzReducer] = useReducer(
    quizzReducer,
    initialQuizzState,
  );

  const isRightAnswer = useMemo(
    () =>
      quizzState.rightAnswerIndex &&
      quizzState.selectedAnswerIndex === quizzState.rightAnswerIndex,
    [quizzState.rightAnswerIndex, quizzState.selectedAnswerIndex],
  );

  const isLastQuizz = useMemo(
    () => quizzState.quizzIndex + 1 === _.size(quizzes),
    [quizzState.quizzIndex, quizzes],
  );

  const onQuit = useCallback(() => {
    dispatchQuizzReducer({ type: 'ON_RESET' });
    navigate(ERoutes.main);
  }, [navigate]);

  useEffect(() => {
    if (!quizzes) {
      return;
    }

    const initialQuizz = _.first(quizzes);

    if (!initialQuizz) {
      return;
    }

    dispatchQuizzReducer({
      type: 'INITIALIZE',
      initialQuizz,
    });
  }, [quizzes]);

  const onSelectAnswer = useCallback(
    async (index: number) => {
      try {
        if (!quizzState.quizz) {
          return;
        }

        setIsLoading(true);

        dispatchQuizzReducer({
          type: 'SELECT_ANSWER',
          selectedIndex: index,
        });

        const response = await submitQuizzAnswer({
          quizId: quizzState.quizz?.id,
          selectedAnswer: index,
        });

        if (!response) {
          return;
        }

        dispatchQuizzReducer({
          type: 'SET_RIGHT_ANSWER',
          rightIndex: response.correctAnswer,
          rightFact: response.correctAnswerFact,
          selectedIndex: index,
        });
      } catch (error: any) {
        console.error('Error while [onSelectAnswer]', error);
      } finally {
        setIsLoading(false);
      }
    },
    [quizzState.quizz, setIsLoading],
  );

  const onClaimRewards = useCallback(async () => {
    try {
      const response = await submitQuizzAnswerBulk(quizzState.answers);

      if (!response) {
        return;
      }

      dispatchQuizzReducer({ type: 'SET_QUIZZ_RESULT', result: response });

      await getQuizzAvailableState();

      setIsClaimRewardModalOpen(true);
    } catch (error) {
      console.error('Error while [onClaimRewards]', error);
    }
  }, [quizzState.answers]);

  const onNextQuizz = useCallback(() => {
    try {
      if (isLastQuizz) {
        onClaimRewards();
        return;
      }

      const nextQuizz = _.get(quizzes, `[${quizzState.quizzIndex + 1}]`);

      if (!nextQuizz) {
        return;
      }

      dispatchQuizzReducer({ type: 'SET_NEXT_QUIZZ', quizz: nextQuizz });
    } catch (error: any) {
      console.error('Error while [onNextQuizz]', error);
    }
  }, [isLastQuizz, onClaimRewards, quizzState.quizzIndex, quizzes]);

  const onOpenConfirmQuitModal = useCallback(
    () => setIsConfirmQuitModalOpen(true),
    [],
  );
  const onCloseConfirmQuitModal = useCallback(
    () => setIsConfirmQuitModalOpen(false),
    [],
  );

  const onCloseClaimRewardModal = useCallback(() => {
    setIsClaimRewardModalOpen(false);
    navigate(ERoutes.main);
  }, [navigate]);

  return (
    <ScreenWrapper>
      <Grid2 sx={materialStyles.screenContainer}>
        <Grid2 sx={materialStyles.quizzContainer}>
          <ContentWrapperWithClose
            wrapperContainerSx={materialStyles.quizzQuestionContainer}
            illustrationName={isRightAnswer ? 'HippoSuccess' : undefined}
            onClose={onOpenConfirmQuitModal}
          >
            <TypographyWithShadow
              shadowType="DARK"
              sx={materialStyles.questionTitle}
            >
              {quizzState.rightAnswerIndex
                ? isRightAnswer
                  ? t('quizz.right')
                  : t('quizz.nope')
                : t('quizz.question_with_size', {
                    page: quizzState.quizzIndex + 1,
                    size: _.size(quizzes),
                  })}
            </TypographyWithShadow>
            <TypographyWithShadow
              sx={materialStyles.questionText}
              shadowType="DARK"
            >
              {quizzState.rightAnswerFact
                ? quizzState.rightAnswerFact
                : quizzState.quizz?.question}
            </TypographyWithShadow>
          </ContentWrapperWithClose>
          <Grid2 sx={materialStyles.quizzAnswersContainer}>
            <QuizzAnswerButton
              answer={quizzState.quizz?.answer_0}
              answerIndex={0}
              onClick={onSelectAnswer}
              selectedAnswerIndex={quizzState.selectedAnswerIndex}
              rightAnswerIndex={quizzState.rightAnswerIndex}
              disabled={quizzState.selectedAnswerIndex !== null}
              isLoading={isLoading}
            />
            <QuizzAnswerButton
              answer={quizzState.quizz?.answer_1}
              answerIndex={1}
              onClick={onSelectAnswer}
              selectedAnswerIndex={quizzState.selectedAnswerIndex}
              rightAnswerIndex={quizzState.rightAnswerIndex}
              disabled={quizzState.selectedAnswerIndex !== null}
              isLoading={isLoading}
            />
            <QuizzAnswerButton
              answer={quizzState.quizz?.answer_2}
              answerIndex={2}
              onClick={onSelectAnswer}
              selectedAnswerIndex={quizzState.selectedAnswerIndex}
              rightAnswerIndex={quizzState.rightAnswerIndex}
              disabled={quizzState.selectedAnswerIndex !== null}
              isLoading={isLoading}
            />
            <QuizzAnswerButton
              answer={quizzState.quizz?.answer_3}
              answerIndex={3}
              onClick={onSelectAnswer}
              selectedAnswerIndex={quizzState.selectedAnswerIndex}
              rightAnswerIndex={quizzState.rightAnswerIndex}
              disabled={quizzState.selectedAnswerIndex !== null}
              isLoading={isLoading}
            />
          </Grid2>
        </Grid2>
        <Grid2 sx={materialStyles.buttonsContainer}>
          <ButtonOutlined shadowType="NONE" onClick={onOpenConfirmQuitModal}>
            {t('quizz.quit_quizz')}
          </ButtonOutlined>
          {quizzState.selectedAnswerIndex !== null ? (
            <ButtonOutlined shadowType="NONE" onClick={onNextQuizz}>
              {isLastQuizz ? t('quizz.claim_rewards') : t('common.next')}
            </ButtonOutlined>
          ) : null}
        </Grid2>
      </Grid2>
      <ConfirmModal
        title={t('quizz.claim_rewards')}
        subtitle={t('quizz.progress_you_have_start_quizz_from_beginning')}
        closeButtonTitle={t('common.back')}
        submitButtonTitle={t('common.quit')}
        onClose={onCloseConfirmQuitModal}
        isOpen={isConfirmQuitModalOpen}
        onSubmit={onQuit}
        backgroundIllustrationName="HippoWithRoad"
      />
      <ClaimRewardsQuizzModal
        result={quizzState.result}
        isOpen={isClaimRewardModalOpen}
        onClose={onCloseClaimRewardModal}
      />
    </ScreenWrapper>
  );
};

const materialStyles: TMaterialStyles = {
  screenContainer: {
    flex: 1,
    paddingX: '16px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    maxWidth: '738px',
    width: '100%',
    margin: 'auto',
  },
  quizzContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    pt: 2,
  },
  quizzQuestionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: '30px',
    flexDirection: 'column',
    height: '219px',
    mx: 'auto',
  },
  questionTitle: {
    fontSize: '31px',
    lineHeight: '32px',
    marginBottom: '25px',
    fontFamily: 'Rubber Duck !important',
    textAlign: 'center',
  },
  questionText: {
    fontSize: '17px',
    lineHeight: '22px',
    textAlign: 'center',
    fontWeight: '600',
  },
  quizzAnswersContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    gap: '22px',
    mt: '40px',
  },
  quizzAnswersContainerRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    flexWrap: 'wrap',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '16px',
    marginBottom: '20px',
  },
};

export default QuizzScreen;
